import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DoCheck,
  Injector,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

import { FbRadioCompositeWidget } from '../../../models';
import { WidgetBase } from '../../../models/widget-base';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TokenToDivPipe } from '../../../helpers/token-to-div.pipe';
import { MatRadioModule } from '@angular/material/radio';
import { ScribeSuggestionIndicatorComponent } from '../shared/scribe-suggestion-indicator/scribe-suggestion-indicator.component';
import { AmendmentFieldIndicatorComponent } from '../shared/amendment-field-indicator/amendment-field-indicator.component';

@Component({
  selector: 'app-fb-radio-composite',
  templateUrl: './fb-radio-composite.component.html',
  styleUrls: ['./fb-radio-composite.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    TokenToDivPipe,
    MatRadioModule,
    ScribeSuggestionIndicatorComponent,
    AmendmentFieldIndicatorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbRadioCompositeComponent extends WidgetBase implements OnInit, OnDestroy, DoCheck {
  public columns;
  private _payloadOriginal;
  private checkInterval: any;
  @Input()
  public override set payload(value) {
    this._payload = value;
    this.makeColumns();
    this._payloadOriginal = JSON.stringify(this.payload);
    this.ready.next(true);
  }

  public override get payload() {
    return this._payload;
  }

  constructor(
    private sanitizer: DomSanitizer,
    private injector: Injector,
    private _cd: ChangeDetectorRef,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.checkInterval = setInterval(() => {
      const currentPayload = JSON.stringify(this._payload);
      if (currentPayload !== this._payloadOriginal) {
        this._payloadOriginal = currentPayload;
        this.makeColumns();
        this._cd.detectChanges();
      }
    }, 500);
  }

  override ngOnDestroy() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
    super.ngOnDestroy();
  }

  ngDoCheck() {
    // TODO: makeColumns must be called on Properties change
    if (JSON.stringify(this._payload) !== this._payloadOriginal) {
      this._payloadOriginal = JSON.stringify(this.payload);
      this.makeColumns();
    }
  }

  public makeColumns() {
    const items = this.payload.properties.items.value;
    const totalItems = items.length;
    const requestedCols = (this.payload.properties.columns || {}).value || 1;
    // Do not render more columns than items to avoid empty columns.
    const cols = Math.max(1, Math.min(requestedCols, totalItems || 1));

    const result: any[] = Array.from({ length: cols }, () => []);
    // Distribute row-wise so items keep their original order across rows.
    for (let i = 0; i < totalItems; i++) {
      result[i % cols].push(items[i]);
    }

    this.columns = result;
  }

  public getStyles() {
    const cols = Math.max(
      1,
      this.columns?.length || (this.payload.properties.columns || {}).value || 1,
    );
    const styles = [
      'flex: 0 0 ' + 100 / cols + '%',
      'width: ' + 100 / cols + '%',
      'min-width: ' + 100 / cols + '%',
      'max-width: ' + 100 / cols + '%',
    ];

    return this.sanitizeStyle(styles.join(';'));
  }

  public sanitizeStyle(unsafeStyle: string): SafeStyle {
    return this.sanitizer.bypassSecurityTrustStyle(unsafeStyle);
  }

  override isValid() {
    return new FbRadioCompositeWidget(this.payload).isValid();
  }
}
